import { NavigationContainerRefWithCurrent, useNavigation } from '@react-navigation/native'
import { LinearGradient } from 'expo-linear-gradient'
import React, { useMemo } from 'react'
import { StyleSheet, TouchableOpacity } from 'react-native'
import { DefaultTheme } from 'styled-components'
import urlRegex from 'url-regex'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'

import { links } from 'utils/constants'
import { formatShortDateTime, timeSince } from 'utils/time'

import { useCommentModal } from 'contexts/CommentModalContext'
import { useFocusMode } from 'contexts/FocusModeContext'
import { useTheme } from 'contexts/ThemeContext'

import { Message } from 'types/chat'
import { UserType } from 'types/user'

interface MessageCardProps {
  isCurrentUser?: boolean
  message: Message
  onMessageLongPress: (reportedAccount: UserType) => void
  isComment?: boolean
  drawerNavigationRef?: NavigationContainerRefWithCurrent<ReactNavigation.RootParamList>
}

const makeStyles = (theme: DefaultTheme, isCurrentUser: boolean, isComment: boolean) =>
  StyleSheet.create({
    chatBox: {
      maxWidth: isComment ? '95%' : '80%',
      marginTop: theme.spacing.xs,
      marginBottom: theme.spacing.xs,
      alignSelf: isCurrentUser && !isComment ? 'flex-end' : 'flex-start',
      borderRadius: theme.borderRadius.sm,
      borderTopRightRadius: isCurrentUser ? 0 : theme.borderRadius.sm,
      borderTopLeftRadius: isCurrentUser ? theme.borderRadius.sm : 0,
      padding: isComment ? 0 : theme.spacing.sm,
    },
    creatorContainer: { alignSelf: isCurrentUser && !isComment ? 'flex-end' : 'flex-start' },
  })

const computeGradientColors = (isCurrentUser: boolean, hasLink: boolean): Array<string> => {
  let startColor = '#D8D8D8'
  let endColor = '#E7EDF3'

  if (isCurrentUser) {
    if (!hasLink) {
      startColor = '#13BCFF'
      endColor = '#6881F9'
    } else {
      startColor = '#E7EDF3'
      endColor = '#D8D8D8'
    }
  }

  return [startColor, endColor]
}

export const MessageCard = ({
  isCurrentUser = false,
  message,
  onMessageLongPress,
  isComment = false,
  drawerNavigationRef,
}: MessageCardProps): JSX.Element => {
  const theme = useTheme()
  const styles = makeStyles(theme, isCurrentUser, isComment)
  const hasLink = urlRegex({ strict: false }).test(message.body)
  const navigation = useNavigation<any>()
  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])
  const commentModalRef = useCommentModal()

  const navigateToProfile = () => {
    if (drawerNavigationRef) {
      drawerNavigationRef.navigate(!isFocusMode ? links.PROFILE_STACK : links.GROUP_STACK, {
        screen: links.MEMBER_PROFILE_PAGE,
        params: { username: message.creator.username },
      })
      commentModalRef?.current?.dismiss()
      return
    }
    navigation.navigate(!isFocusMode ? links.PROFILE_STACK : links.GROUP_STACK, {
      screen: links.MEMBER_PROFILE_PAGE,
      params: { username: message.creator.username },
    })
  }

  return (
    <Box flexDirection='row' margin={{ bottom: 'smd', left: 'smd', right: 'smd' }}>
      <Box testID='AvatarContainer' margin={{ top: isComment ? 0 : 20 }}>
        <Pressable onPress={navigateToProfile}>
          <Box flexDirection='row' align='center'>
            {(!isCurrentUser || isComment) && (
              <Box margin={{ right: isComment ? 'smd' : 'sm' }}>
                <Avatar image={message.creator.avatar} height={32} width={32} />
              </Box>
            )}
          </Box>
        </Pressable>
      </Box>

      <Box flex={1} flexDirection='column'>
        <Box
          testID='MessageMetaContainer'
          flexDirection='row'
          align='center'
          maxWidth='80%'
          style={styles.creatorContainer}
        >
          {(!isCurrentUser || isComment) && (
            <Text
              numberOfLines={1}
              bold
              color={isComment ? 'greyDarkest' : 'greyDark'}
              fontSize={isComment ? 'sm' : 'xs'}
            >
              {isComment
                ? `${message.creator.username}`
                : `${message.creator.firstName} ${message.creator.lastName}`}
            </Text>
          )}
          <Box margin={{ horizontal: 'xs' }} />
          <Text numberOfLines={1} fontSize='xs' color={!isCurrentUser ? 'greyDark' : 'greyDarkest'}>
            {isComment ? timeSince(message.created) : formatShortDateTime(message.created)}
          </Text>
        </Box>

        <TouchableOpacity
          onPress={(e) => e.preventDefault()}
          onLongPress={() => (!isCurrentUser ? onMessageLongPress(message.creator) : null)}
          accessibilityRole='text'
        >
          <LinearGradient
            colors={!isComment ? computeGradientColors(isCurrentUser, hasLink) : []}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.chatBox}
          >
            <Text
              fontSize='sm'
              fontWeight='medium'
              color={!isCurrentUser || isComment ? 'black' : 'white'}
              withLinks={hasLink}
              onLinkLongPress={() => (!isCurrentUser ? onMessageLongPress(message.creator) : null)}
            >
              {message.body}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </Box>
    </Box>
  )
}
